import { useState } from "react";
import { Link } from "react-router-dom";
import { categories, site } from "@/mocks/siteData";

const SUBMIT_URL = "https://readdy.ai/api/form/dae0lhptto0ceqqngnb0";

export default function SiteFooter() {
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt") || "").trim();
    setStatus("loading");
    setMessage("");

    // honeypot hit -> show generic success without submitting
    if (honeypot) {
      setStatus("ok");
      form.reset();
      return;
    }

    fd.delete("website_alt");
    const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        (parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "").toString();
      const isSpam = responseText.toLowerCase().includes("spam");

      if (res.ok && code === "OK" && !isSpam) {
        setStatus("ok");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，订阅未成功，请稍后重试。");
    }
  }

  return (
    <footer id="subscribe" className="relative overflow-hidden bg-background-900 text-background-100">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent"></div>
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6">
        {/* subscribe banner */}
        <div className="grid gap-8 rounded-2xl border border-background-50/10 bg-foreground-950/40 p-6 md:grid-cols-2 md:items-center md:p-10">
          <div>
            <p className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium tracking-wider text-primary-300">
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              每周一封 · 探秘简报
            </p>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              把地下新发现，第一时间送到你手里
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100/75">
              订阅「猎奇网探秘简报」，每周精选一篇历史秘闻与考古发现深度稿，
              只在简报里出现的独家考据、实地手记与下期预告。
            </p>
          </div>
          <form data-readdy-form onSubmit={onSubmit} className="w-full">
            <label htmlFor="footer-email" className="sr-only">
              邮箱地址
            </label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                id="footer-email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full rounded-md border border-background-50/15 bg-background-50/5 px-4 py-3 text-sm text-background-50 outline-none transition placeholder:text-background-100/40 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
              >
                {status === "loading" ? (
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                ) : (
                  <i className="ri-send-plane-fill" aria-hidden="true"></i>
                )}
                立即订阅
              </button>
            </div>
            {/* honeypot */}
            <input
              type="text"
              name="website_alt"
              className="web-alt-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            {status === "ok" && (
              <p className="mt-3 text-sm text-primary-300">
                订阅成功，期待与你在地下的下一次相遇。
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-accent-300">{message}</p>
            )}
            <p className="mt-3 text-xs text-background-100/50">
              订阅即代表同意接收我们的邮件，可随时退订。你的邮箱仅用于发送简报。
            </p>
          </form>
        </div>

        {/* link columns */}
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded bg-primary-500 text-background-50">
                <i className="ri-ancient-pavilion-line" aria-hidden="true"></i>
              </span>
              <span className="font-heading text-lg font-bold tracking-[0.18em] text-background-50">
                {site.name}
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-background-100/70">{site.slogan}</p>
            <p className="mt-3 max-w-xs text-xs leading-relaxed text-background-100/55">
              追踪历史秘闻、考古发现与古墓背后的真相，还原被时间掩埋的奇事。
            </p>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold text-background-50">主题栏目</h3>
            <ul className="space-y-2.5 text-sm text-background-100/70">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/explore/${c.slug}`}
                    className="transition-colors hover:text-primary-300"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold text-background-50">快速导航</h3>
            <ul className="space-y-2.5 text-sm text-background-100/70">
              <li>
                <Link to="/" className="transition-colors hover:text-primary-300">
                  首页
                </Link>
              </li>
              <li>
                <Link to="/about" className="transition-colors hover:text-primary-300">
                  关于我们
                </Link>
              </li>
              <li>
                <a href="#subscribe" className="transition-colors hover:text-primary-300">
                  订阅简报
                </a>
              </li>
              <li>
                <Link to="/" className="transition-colors hover:text-primary-300">
                  编辑团队
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold text-background-50">联系编辑部</h3>
            <ul className="space-y-3 text-sm text-background-100/70">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-400" aria-hidden="true"></i>
                <a
                  href={`mailto:${site.contact.email}`}
                  className="transition-colors hover:text-primary-300"
                >
                  {site.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-weibo-line text-primary-400" aria-hidden="true"></i>
                <span>@{site.contact.weibo}</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-primary-400" aria-hidden="true"></i>
                <span>每周一、周四更新</span>
              </li>
            </ul>
          </div>
        </div>

        {/* bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-background-50/10 pt-6 sm:flex-row">
          <p className="text-xs text-background-100/50">
            © {new Date().getFullYear()} {site.name} · 尊重史料，审慎求证
          </p>
          <div className="flex items-center gap-4 text-xs text-background-100/50">
            <Link to="/about" className="transition-colors hover:text-primary-300">
              内容原则
            </Link>
            <Link to="/about" className="transition-colors hover:text-primary-300">
              免责声明
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}